import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Link2, Copy, Plus, Trash2, RefreshCw, AlertCircle, X,
  Download, Search, Sun, Moon, MessageCircle, Mail, MessageSquare, Globe,
} from 'lucide-react';
import { useTheme } from '../components/Navbar';
import useAutoLogout from '../hooks/useAutoLogout';
import Toast from '../components/Toast';
import API_BASE from '../config';

const TK = {
  light: {
    pageBg:'#F5F6FA', card:'#FFFFFF', border:'#ECEEF2',
    text:'#0F1623', sub:'#6B7280', muted:'#9CA3AF',
    accent:'#5B4EE8', accentBg:'#EEF0FD', accentTxt:'#4338CA',
    row:'#FAFBFC', rowBdr:'#F0F2F5', input:'#FFFFFF',
    btnPri:'#5B4EE8', btnPriH:'#4C3FD4', btnPriTxt:'#fff',
    btnSec:'#fff', btnSecTxt:'#374151', btnSecBdr:'#ECEEF2',
    chip:'#F3F4F6', chipTxt:'#374151', overlay:'rgba(15,22,35,0.32)',
    toggleBg:'#ECEEF2', toggleActive:'#fff',
    errBg:'#FEF2F2', errBdr:'#FECACA', errTxt:'#DC2626',
  },
  dark: {
    pageBg:'#0D0D14', card:'#131320', border:'#1E1E2E',
    text:'#F1F2F6', sub:'#8B8FA8', muted:'#4B5263',
    accent:'#7C72FF', accentBg:'rgba(124,114,255,0.12)', accentTxt:'#A5A0FF',
    row:'#18182A', rowBdr:'#1E1E2E', input:'#18182A',
    btnPri:'#6C63FF', btnPriH:'#5B53E8', btnPriTxt:'#fff',
    btnSec:'#1A1A2A', btnSecTxt:'#D1D5DB', btnSecBdr:'#1E1E2E',
    chip:'#1A1A2A', chipTxt:'#C7CBDA', overlay:'rgba(0,0,0,0.55)',
    toggleBg:'#1A1A2A', toggleActive:'#1E1E2E',
    errBg:'rgba(220,38,38,0.12)', errBdr:'rgba(220,38,38,0.3)', errTxt:'#F87171',
  },
};

const CHANNEL_META = {
  whatsapp: { label:'WhatsApp', color:'#22C55E', Icon: MessageCircle },
  email:    { label:'Email',    color:'#3B82F6', Icon: Mail },
  sms:      { label:'SMS',      color:'#F59E0B', Icon: MessageSquare },
};

const FILTERS = ['All', 'Used', 'Unused'];

const getToken = () =>
  localStorage.getItem('martech_token') || localStorage.getItem('token') ||
  sessionStorage.getItem('martech_token') || sessionStorage.getItem('token') || '';

function formatIST(d) {
  if (!d) return '—';
  const s = d.toString();
  const normalized = s.endsWith('Z') ? s : s.replace(' ', 'T') + 'Z';
  const dt = new Date(normalized);
  if (isNaN(dt)) return '—';
  return dt.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function truncate(str, n = 40) {
  if (!str) return '—';
  return str.length > n ? str.slice(0, n) + '…' : str;
}

function csvEscape(v) {
  if (v == null) return '';
  const s = String(v).replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${s}"` : s;
}
function buildClicksCsv(rows) {
  const headers = [
    'clicked_at_ist', 'profile_id', 'email', 'phone', 'first_name', 'last_name',
    'ip_address', 'user_agent', 'utm_source', 'utm_medium', 'utm_campaign',
  ];
  const lines = [headers.join(',')];
  rows.forEach(r => {
    lines.push([
      formatIST(r.clicked_at), r.profile_id, r.email, r.phone, r.first_name, r.last_name,
      r.ip_address, r.user_agent, r.utm_source, r.utm_medium, r.utm_campaign,
    ].map(csvEscape).join(','));
  });
  return lines.join('\n');
}
function downloadCsv(csv, code) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `clicks_${code}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function ThemeToggle({ tk }) {
  const { theme, setTheme } = useTheme();
  const btn = (t, Icon, label) => (
    <button
      type="button" key={t} onClick={() => setTheme(t)}
      style={{
        display: 'flex', alignItems: 'center', gap: 5,
        background: theme === t ? tk.toggleActive : 'transparent',
        border: 'none', borderRadius: 6, padding: '5px 10px', cursor: 'pointer',
        fontSize: 12, fontWeight: 500, color: theme === t ? tk.text : tk.sub,
        boxShadow: theme === t ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
        transition: 'all 0.15s', whiteSpace: 'nowrap',
      }}
    >
      <Icon size={13} /> {label}
    </button>
  );
  return (
    <div style={{ display: 'flex', background: tk.toggleBg, borderRadius: 8, padding: 3 }}>
      {btn('light', Sun, 'Light')}{btn('dark', Moon, 'Dark')}
    </div>
  );
}

function StatCard({ tk, label, value, sub, loading, isMobile }) {
  return (
    <div style={{
      background: tk.card,
      border: `1px solid ${tk.border}`,
      borderRadius: 16,
      padding: isMobile ? '14px 16px' : '20px',
    }}>
      <div style={{ width: 28, height: 28, borderRadius: 10, background: tk.accentBg, marginBottom: 10 }} />
      {loading
        ? <div style={{ height: 28, width: 80, borderRadius: 8, background: tk.row, marginBottom: 4 }} />
        : <p style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: tk.text, margin: 0 }}>{value ?? '—'}</p>}
      <p style={{ fontSize: 11, color: tk.sub, margin: '4px 0 0' }}>{label}</p>
      {sub && <p style={{ fontSize: 10, color: tk.muted, margin: '2px 0 0' }}>{sub}</p>}
    </div>
  );
}

function ChannelBadge({ tk, channel, campaign }) {
  const key = (channel || '').toLowerCase();
  const meta = CHANNEL_META[key];
  if (!meta && !campaign) {
    return <span style={{ fontSize: 12, color: tk.muted }}>—</span>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {meta ? (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 10, fontWeight: 700, padding: '2px 8px',
          borderRadius: 999, width: 'fit-content',
          background: `${meta.color}1A`, color: meta.color,
          border: `1px solid ${meta.color}33`,
        }}>
          <meta.Icon size={11} />
          {meta.label}
        </span>
      ) : (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 10, fontWeight: 700, padding: '2px 8px',
          borderRadius: 999, width: 'fit-content',
          background: tk.chip, color: tk.chipTxt,
          border: `1px solid ${tk.border}`,
        }}>
          <Globe size={11} />
          {channel || 'other'}
        </span>
      )}
      {campaign && (
        <span style={{ fontSize: 11, fontWeight: 500, color: tk.sub }} title={campaign}>
          {truncate(campaign, 24)}
        </span>
      )}
    </div>
  );
}

/* ---- Mobile card for a single link ---- */
function LinkCard({ tk, link, onCopy, onExport, onDelete, exportingCode }) {
  const id = link.short_link_id ?? link.id;
  const code = link.short_code || null;
  const urlVal = link.original_url || '';
  const clicks = parseInt(link.click_count) || 0;
  const canExport = !!code && clicks > 0;
  const exporting = exportingCode !== null && exportingCode === code;

  return (
    <div style={{
      background: tk.card,
      border: `1px solid ${tk.border}`,
      borderRadius: 16,
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
    }}>
      {/* Top row: code + clicks */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {code ? (
          <button
            onClick={() => onCopy(code)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            }}
          >
            <span style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 700, color: tk.accent }}>{code}</span>
            <Copy size={13} style={{ color: tk.muted }} />
          </button>
        ) : (
          <span style={{ fontFamily: 'monospace', fontSize: 13, color: tk.muted }}>—</span>
        )}
        <span style={{
          fontSize: 18, fontWeight: 900,
          color: clicks > 0 ? '#F59E0B' : tk.muted,
        }}>
          {clicks} <span style={{ fontSize: 10, fontWeight: 500, color: tk.muted }}>clicks</span>
        </span>
      </div>

      {/* URL */}
      <p style={{ fontSize: 12, color: tk.sub, margin: 0, wordBreak: 'break-all' }}>
        {truncate(urlVal, 60)}
      </p>

      {/* Channel + Created */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <ChannelBadge tk={tk} channel={link.channel} campaign={link.campaign_name} />
        <span style={{ fontSize: 11, color: tk.muted }}>{formatIST(link.created_at)}</span>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={() => onExport(code, clicks)}
          disabled={!canExport || exporting}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            padding: '8px 12px', borderRadius: 10, fontSize: 12, fontWeight: 700,
            border: 'none', cursor: canExport && !exporting ? 'pointer' : 'not-allowed',
            opacity: !canExport || exporting ? 0.4 : 1,
            color: tk.accent, background: tk.accentBg,
          }}
        >
          {exporting
            ? <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} />
            : <Download size={13} />}
          Export
        </button>
        <button
          onClick={() => onDelete(id)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700,
            border: 'none', cursor: 'pointer',
            color: tk.sub, background: tk.chip,
          }}
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

export default function ShortLinks() {
  const navigate = useNavigate();
  useAutoLogout();
  const { theme } = useTheme();
  const tk = TK[theme];

  /* ---- responsive: JS-driven, pure inline styles ---- */
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const token = getToken();

  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [originalUrl, setOriginalUrl] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [channel, setChannel] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [exportingCode, setExportingCode] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, variant = 'green') => setToast({ msg, variant });

  const fetchLinks = useCallback(async () => {
    if (!token) { navigate('/'); return; }
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/v1/links`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (d.success !== false) {
        setLinks(Array.isArray(d.links) ? d.links : Array.isArray(d) ? d : []);
      } else {
        setError(d.message || d.error || 'Failed to load links');
      }
    } catch {
      setError('Network error — could not load short links');
    }
    setLoading(false);
  }, [token, navigate]);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const handleCreate = async () => {
    if (!originalUrl.trim()) return;
    setSubmitting(true);
    try {
      const body = { original_url: originalUrl.trim() };
      if (campaignId.trim()) body.campaign_id = campaignId.trim();
      if (channel) body.channel = channel;
      const r = await fetch(`${API_BASE}/api/v1/links/shorten`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (r.ok && d.success !== false) {
        setModalOpen(false);
        setOriginalUrl(''); setCampaignId(''); setChannel('');
        showToast('Short link created', 'green');
        fetchLinks();
      } else {
        showToast(d.message || d.error || 'Failed to create link', 'red');
      }
    } catch {
      showToast('Network error', 'red');
    }
    setSubmitting(false);
  };

  const handleDelete = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/api/v1/links/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        setLinks(prev => prev.filter(l => (l.short_link_id ?? l.id) !== id));
        showToast('Link deleted', 'green');
      } else {
        showToast('Failed to delete link', 'red');
      }
    } catch {
      showToast('Network error', 'red');
    }
  };

  const copyShortUrl = (code) => {
    if (!code) return;
    const url = `${window.location.origin}/s/${code}`;
    navigator.clipboard.writeText(url).then(() => showToast(`Copied: ${url}`, 'blue'));
  };

  const handleExport = async (code, clicks) => {
    if (!code || clicks <= 0) return;
    setExportingCode(code);
    try {
      const r = await fetch(`${API_BASE}/api/v1/links/${code}/clicks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (r.ok && d.success && Array.isArray(d.clicks) && d.clicks.length) {
        downloadCsv(buildClicksCsv(d.clicks), code);
        showToast(`Exported ${d.count} click${d.count !== 1 ? 's' : ''}`, 'green');
      } else if (d.success && d.count === 0) {
        showToast('No click records to export', 'blue');
      } else {
        showToast(d.error || 'Export failed', 'red');
      }
    } catch {
      showToast('Network error during export', 'red');
    }
    setExportingCode(null);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return links.filter(l => {
      const used = (parseInt(l.click_count) || 0) > 0;
      if (filter === 'Used' && !used) return false;
      if (filter === 'Unused' && used) return false;
      if (!q) return true;
      const hay = [l.short_code, l.original_url, l.campaign_name, l.channel]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [links, filter, search]);

  const totalLinks = links.length;
  const totalClicks = links.reduce((s, l) => s + (parseInt(l.click_count) || 0), 0);
  const usedLinks = links.filter(l => (parseInt(l.click_count) || 0) > 0).length;
  const unusedLinks = totalLinks - usedLinks;

  const inputStyle = {
    background: tk.input, border: `1px solid ${tk.border}`, color: tk.text,
    borderRadius: 12, outline: 'none', width: '100%',
    padding: '10px 16px', fontSize: 14,
  };

  return (
    <div style={{ minHeight: '100vh', background: tk.pageBg }}>
      {isMobile && <div style={{ height: 56 }} />}
      {toast && <Toast message={toast.msg} variant={toast.variant} onDismiss={() => setToast(null)} />}

      <main style={{
        padding: isMobile ? '16px 16px 32px' : '32px 24px',
        paddingTop: isMobile ? 72 : 32,
        maxWidth: 1200,
        margin: '0 auto',
      }}>

        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          marginBottom: isMobile ? 20 : 32,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 16 : 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: tk.accentBg, flexShrink: 0,
            }}>
              <Link2 size={20} style={{ color: tk.accent }} />
            </div>
            <div>
              <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: tk.text, margin: 0 }}>Short Links</h1>
              <p style={{ fontSize: 13, color: tk.sub, margin: '2px 0 0' }}>Track, filter and export your short URLs</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: isMobile ? '100%' : 'auto' }}>
            <ThemeToggle tk={tk} />
            <button
              onClick={() => setModalOpen(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: isMobile ? '10px 16px' : '10px 20px',
                borderRadius: 12, fontSize: 13, fontWeight: 700,
                background: tk.btnPri, color: tk.btnPriTxt,
                border: 'none', cursor: 'pointer',
                flex: isMobile ? 1 : 'none',
                justifyContent: 'center',
              }}
            >
              <Plus size={16} />
              Create Link
            </button>
          </div>
        </div>

        {/* Stat cards — 2 col on mobile, 4 on desktop */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: isMobile ? 10 : 16,
          marginBottom: 20,
        }}>
          <StatCard tk={tk} label="Total Links" value={totalLinks} sub="All short links" loading={loading} isMobile={isMobile} />
          <StatCard tk={tk} label="Total Clicks" value={totalClicks} sub="Sum of all clicks" loading={loading} isMobile={isMobile} />
          <StatCard tk={tk} label="Used Links" value={usedLinks} sub="At least 1 click" loading={loading} isMobile={isMobile} />
          <StatCard tk={tk} label="Unused Links" value={unusedLinks} sub="Zero clicks" loading={loading} isMobile={isMobile} />
        </div>

        {/* Error banner */}
        {error && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '14px 20px', borderRadius: 16, marginBottom: 20,
            fontSize: 13, fontWeight: 600,
            background: tk.errBg, border: `1px solid ${tk.errBdr}`, color: tk.errTxt,
          }}>
            <AlertCircle size={16} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1 }}>{error}</span>
            <button
              onClick={fetchLinks}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                background: 'none', border: 'none', cursor: 'pointer', color: tk.errTxt,
              }}
            >
              <RefreshCw size={12} /> Retry
            </button>
          </div>
        )}

        {/* Controls: filter tabs + search */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'space-between',
          gap: 10,
          marginBottom: 16,
        }}>
          {/* Filter tabs */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 2,
            padding: 4, borderRadius: 12, background: tk.toggleBg,
          }}>
            {FILTERS.map(f => {
              const active = filter === f;
              const count = f === 'Used' ? usedLinks : f === 'Unused' ? unusedLinks : totalLinks;
              return (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  style={{
                    flex: isMobile ? 1 : 'none',
                    padding: '6px 14px', borderRadius: 8,
                    fontSize: 12, fontWeight: 700,
                    border: 'none', cursor: 'pointer',
                    background: active ? tk.toggleActive : 'transparent',
                    color: active ? tk.text : tk.sub,
                    boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                    transition: 'all 0.15s',
                  }}
                >
                  {f} <span style={{ color: active ? tk.accent : tk.muted }}>{loading ? '' : count}</span>
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div style={{ position: 'relative', flex: isMobile ? 'none' : 1, maxWidth: isMobile ? '100%' : 320 }}>
            <Search size={15} style={{
              position: 'absolute', left: 12, top: '50%',
              transform: 'translateY(-50%)', color: tk.muted,
            }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search code, URL, campaign…"
              style={{ ...inputStyle, paddingLeft: 38 }}
            />
          </div>
        </div>

        {/* Links list container */}
        <div style={{
          background: tk.card,
          border: `1px solid ${tk.border}`,
          borderRadius: 16,
          overflow: 'hidden',
        }}>
          {/* Section header */}
          <div style={{
            padding: isMobile ? '14px 16px' : '16px 24px',
            borderBottom: `1px solid ${tk.border}`,
          }}>
            <h2 style={{ fontSize: 14, fontWeight: 900, color: tk.text, margin: 0 }}>All Short Links</h2>
            <p style={{ fontSize: 11, color: tk.sub, margin: '2px 0 0' }}>
              {loading ? '—' : `${filtered.length} of ${totalLinks} shown`}
            </p>
          </div>

          {loading ? (
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Array(5).fill(null).map((_, i) => (
                <div key={i} style={{ height: isMobile ? 120 : 48, borderRadius: 12, background: tk.row }} />
              ))}
            </div>
          ) : !error && filtered.length === 0 ? (
            <div style={{
              padding: '60px 20px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }}>
              <Link2 size={36} style={{ color: tk.muted, marginBottom: 12 }} />
              <p style={{ fontSize: 13, fontWeight: 600, color: tk.sub, margin: 0 }}>
                {links.length === 0 ? 'No short links yet' : 'No links match this filter'}
              </p>
              <p style={{ fontSize: 11, color: tk.muted, margin: '4px 0 0', textAlign: 'center' }}>
                {links.length === 0 ? 'Click "Create Link" to add your first short URL' : 'Try a different filter or search'}
              </p>
            </div>
          ) : !error ? (
            isMobile ? (
              /* ---- MOBILE: card list ---- */
              <div style={{ padding: '12px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {filtered.map((link) => (
                  <LinkCard
                    key={link.short_link_id ?? link.id ?? link.short_code}
                    tk={tk}
                    link={link}
                    onCopy={copyShortUrl}
                    onExport={handleExport}
                    onDelete={handleDelete}
                    exportingCode={exportingCode}
                  />
                ))}
              </div>
            ) : (
              /* ---- DESKTOP: table ---- */
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse', minWidth: 860 }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${tk.border}` }}>
                      {['Short Code', 'Original URL', 'Campaign / Channel', 'Clicks', 'Created', 'Actions'].map((col, i) => (
                        <th key={i} style={{
                          padding: '10px 20px', textAlign: 'left',
                          fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
                          color: tk.muted, background: tk.row,
                        }}>
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((link) => {
                      const id = link.short_link_id ?? link.id;
                      const code = link.short_code || null;
                      const urlVal = link.original_url || '';
                      const clicks = parseInt(link.click_count) || 0;
                      const canExport = !!code && clicks > 0;
                      const exporting = exportingCode !== null && exportingCode === code;

                      return (
                        <tr key={id ?? code} style={{ borderBottom: `1px solid ${tk.rowBdr}` }}>
                          <td style={{ padding: '14px 20px' }}>
                            {code ? (
                              <button
                                onClick={() => copyShortUrl(code)}
                                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                              >
                                <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: tk.accent }}>{code}</span>
                                <Copy size={12} style={{ color: tk.muted }} />
                              </button>
                            ) : (
                              <span style={{ fontFamily: 'monospace', fontSize: 12, color: tk.muted }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: '14px 20px' }}>
                            <span style={{ fontSize: 12, color: tk.sub }} title={urlVal}>{truncate(urlVal, 40)}</span>
                          </td>
                          <td style={{ padding: '14px 20px' }}>
                            <ChannelBadge tk={tk} channel={link.channel} campaign={link.campaign_name} />
                          </td>
                          <td style={{ padding: '14px 20px' }}>
                            <span style={{ fontWeight: 900, fontSize: 13, color: clicks > 0 ? '#F59E0B' : tk.muted }}>{clicks}</span>
                          </td>
                          <td style={{ padding: '14px 20px', fontSize: 12, whiteSpace: 'nowrap', color: tk.sub }}>
                            {formatIST(link.created_at)}
                          </td>
                          <td style={{ padding: '14px 20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <button
                                onClick={() => handleExport(code, clicks)}
                                disabled={!canExport || exporting}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 6,
                                  padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                                  border: 'none', cursor: canExport && !exporting ? 'pointer' : 'not-allowed',
                                  opacity: !canExport || exporting ? 0.4 : 1,
                                  color: tk.accent, background: tk.accentBg,
                                }}
                              >
                                {exporting ? <RefreshCw size={12} /> : <Download size={12} />}
                                Export
                              </button>
                              <button
                                onClick={() => handleDelete(id)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 6,
                                  padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                                  border: 'none', cursor: 'pointer',
                                  color: tk.sub, background: tk.chip,
                                }}
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          ) : null}
        </div>
      </main>

      {/* Create Link Modal */}
      {modalOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 50,
          display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
          padding: isMobile ? 0 : 16,
        }}>
          <div
            style={{ position: 'absolute', inset: 0, background: tk.overlay }}
            onClick={() => setModalOpen(false)}
          />
          <div style={{
            position: 'relative', zIndex: 10,
            width: '100%', maxWidth: isMobile ? '100%' : 440,
            background: tk.card, border: `1px solid ${tk.border}`,
            borderRadius: isMobile ? '20px 20px 0 0' : 20,
            padding: isMobile ? '24px 20px 36px' : 24,
          }}>
            {/* Modal handle on mobile */}
            {isMobile && (
              <div style={{
                width: 36, height: 4, borderRadius: 2,
                background: tk.border, margin: '-12px auto 20px',
              }} />
            )}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 32, height: 32, borderRadius: 10, background: tk.accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Link2 size={16} style={{ color: tk.accent }} />
                </div>
                <h2 style={{ fontSize: 15, fontWeight: 900, color: tk.text, margin: 0 }}>Create Short Link</h2>
              </div>
              <button onClick={() => setModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: tk.muted }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: tk.sub, marginBottom: 6 }}>
                  Original URL <span style={{ color: tk.errTxt }}>*</span>
                </label>
                <input
                  type="url"
                  value={originalUrl}
                  onChange={e => setOriginalUrl(e.target.value)}
                  placeholder="https://example.com/very/long/path"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: tk.sub, marginBottom: 6 }}>
                  Campaign Reference <span style={{ color: tk.muted }}>(optional)</span>
                </label>
                <input
                  type="text"
                  value={campaignId}
                  onChange={e => setCampaignId(e.target.value)}
                  placeholder="e.g. diwali_sms_2026"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: tk.sub, marginBottom: 6 }}>
                  Channel <span style={{ color: tk.muted }}>(optional)</span>
                </label>
                <select
                  value={channel}
                  onChange={e => setChannel(e.target.value)}
                  style={inputStyle}
                >
                  <option value="">— None —</option>
                  <option value="sms">SMS</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="email">Email</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  style={{
                    flex: 1, padding: '11px 16px', borderRadius: 12,
                    fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    background: tk.btnSec, color: tk.btnSecTxt,
                    border: `1px solid ${tk.btnSecBdr}`,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={submitting || !originalUrl.trim()}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    padding: '11px 16px', borderRadius: 12,
                    fontSize: 13, fontWeight: 700, cursor: submitting || !originalUrl.trim() ? 'not-allowed' : 'pointer',
                    background: tk.btnPri, color: tk.btnPriTxt, border: 'none',
                    opacity: submitting || !originalUrl.trim() ? 0.6 : 1,
                  }}
                >
                  {submitting ? <RefreshCw size={15} /> : <Plus size={15} />}
                  {submitting ? 'Creating…' : 'Create Link'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}